import {
    AfterViewInit,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import moment from 'moment';
import { ReplaySubject } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { Contract } from '../../account/account-allocations';
import * as fromLookups from '../../lookups';
import { CalcType, IServiceItem, ITask, IUserGroup } from '../../lookups';
import { IStatusMessage, Process, SelectItem, Table } from '../../shared';
import { DisplayErrors } from '../../shared/errors-sidenav';
import { LoggingService } from '../../shared/services/logging/logging.service';
import { CostCenter } from '../CostCenter';
import { SessionDates } from '../SessionDates';
import { ContractCumulative } from '../store/contract-cumulative.model';
import { Users } from '../store/users.model';
import { columnResize } from '../../shared/columnResize';
import * as fromReports from '../../reporting/store';
import { ReportParams } from '../../reporting/store/report-params.model';
import * as XLSX from 'xlsx';
@Component({
    selector: 'ef-contract-cumulative-presenter',
    templateUrl: './contract-cumulative-presenter.component.html',
    styleUrls: ['./contract-cumulative-presenter.component.scss'],
    standalone: false
})
export class ContractCumulativePresenterComponent
    extends DisplayErrors<ContractCumulative>
    implements OnInit, OnDestroy, AfterViewInit
{
    @Output() RunReports = new ReplaySubject<{
        reports: fromReports.Report[];
        queryparams: any;
        params: Partial<ReportParams>;
    }>(1);

    @Input() reports: fromReports.Report[];

    SelectedErrorSubject = new ReplaySubject<IStatusMessage>(1);
    selectedAsOfDate: any;
    deletePressed = false;
    private ContractCumsSubject = new ReplaySubject<Array<ContractCumulative>>(
        1
    );
    private _contractcums: Array<ContractCumulative>;
    @Input() set contractcums(value: Array<ContractCumulative>) {
        if (value && value.length > 0) {
            value = value.map(x => ({
                ...x,
                TaskName: this.findTask(x),
                TypeName: this.findTxnType(x),
                EmployeeName: this.findEmployee(x),
                ServiceItem: this.findServiceItem(x)
            }));
            this._contractcums = value;
            if (
                this._contractcums.some(
                    x =>
                        x.Cumstatus == 'Building CTD' ||
                        x.Cumstatus == 'building CTD'
                ) == true
            ) {
                this.log.verbose('Building CTD exists - start refresh timer');
                this.showTimer = true;
                this.Timer.next({
                    timer: this.showTimer,
                    date: this.editFormGroup.controls['SessionDates'].value
                });
            } else {
                this.showTimer = false;
                this.Timer.next({
                    timer: this.showTimer,
                    date: this.editFormGroup.controls['SessionDates'].value
                });
                this.log.verbose(
                    'Building CTD does not exist - end refresh timer'
                );
                this.IsFeatureLoading = false;

                // If we just built the table and received new data, enable edit mode
                // Only enable if we're not already in edit mode and not already setting it up
                if (this.buildPressed && !this.editModeIsEnabled && !this.isSettingUpEditMode) {
                    this.isSettingUpEditMode = true;
                    setTimeout(() => {
                        this.editFormGroup.controls['EditMode'].patchValue(true);
                        this.isSettingUpEditMode = false;
                    }, 100);
                }
            }            // if (this.editModeIsEnabled == true && this.buildPressed == false) {
            //     this.tableItemSource = this._contractcums.filter(
            //         x =>
            //             moment(x.Txndate).format('MM/DD/YY') ==
            //             this.editFormGroup.controls['SessionDates'].value
            //     );
            // } else {
            //     this.tableItemSource = value;
            // }
            this.tableItemSource = value;
            this.ContractCumsSubject.next(value);
            this.IsLoading = false;
        }
    }
    get contractcums(): Array<ContractCumulative> {
        return this._contractcums;
    }

    private ContractsSubject = new ReplaySubject<Array<Contract>>(1);
    private _contracts: Array<Contract>;
    @Input() set contracts(value: Array<Contract>) {
        if (value) {
            this._contracts = value;
            const filteredContracts = this._contracts.filter(
                x => x.ContractType != 'LOC'
            );
            this.ContractsSubject.next(value);
            this.contractsSelectItem = filteredContracts.map(x => ({
                value: x.ContractListID,
                label: x.ContractDesc
            }));
        }
    }
    get contracts(): Array<Contract> {
        return this._contracts;
    }

    private TasksSubject = new ReplaySubject<Array<ITask>>(1);
    private _tasks: Array<ITask>;
    @Input() set tasks(value: Array<ITask>) {
        if (value) {
            this._tasks = value;
            const filteredTasks = this._tasks.filter(
                x => x.ContractType != 'LOC'
            );
            this.TasksSubject.next(value);
            this.tasksSelectItem = filteredTasks.map(x => ({
                value: x.TaskListID,
                label: x.TaskLongName
            }));
            this.tasksInTableSelectItem = filteredTasks.map(x => ({
                value: x.TaskListID,
                label: x.TaskLongName
            }));
        }
    }
    get tasks(): Array<ITask> {
        return this._tasks;
    }

    private TxnTypesSubject = new ReplaySubject<Array<CalcType>>(1);
    private _txntypes: Array<CalcType>;
    @Input() set txntypes(value: Array<CalcType>) {
        if (value) {
            this._txntypes = value;
            const filteredTxnTypes = this._txntypes.filter(
                x => x.CostType == this.doi
            );
            this.TxnTypesSubject.next(value);
            this.txnTypesSelectItem = filteredTxnTypes.map(x => ({
                value: x.CalcType,
                label: x.CalcDesc
            }));
            this.txnTypesInTableSelectItem = filteredTxnTypes.map(x => ({
                value: x.CalcType,
                label: x.CalcDesc
            }));
        }
    }
    get txntypes(): Array<CalcType> {
        return this._txntypes;
    }

    private DirectReportsSubject = new ReplaySubject<Array<Partial<Users>>>(1);
    private _directreports: Array<Partial<Users>>;
    @Input() set directreports(value: Array<Partial<Users>>) {
        if (value) {
            value.splice(0, 0, {
                EmployeeListID: 'SYS000000000000000000000000000000000',
                EmployeeName: 'Not applicable'
            });
            this._directreports = value;
            this.DirectReportsSubject.next(value);
            this.directReportsSelectItem = this._directreports.map(x => ({
                value: x.EmployeeListID,
                label: x.EmployeeName
            }));
        }
    }
    get directreports(): Array<Partial<Users>> {
        return this._directreports;
    }

    private ServiceItemsSubject = new ReplaySubject<Array<IServiceItem>>(1);
    private _serviceitems: Array<IServiceItem>;
    @Input() set serviceitems(value: Array<IServiceItem>) {
        if (value) {
            this._serviceitems = value;
            this.ServiceItemsSubject.next(value);
            this.serviceItemsSelectItem = this._serviceitems.map(x => ({
                value: x.SIListID,
                label: x.ServiceItem
            }));
            this.log.verbose('si count = ' + this._serviceitems.length);
        }
    }
    get serviceitems(): Array<IServiceItem> {
        return this._serviceitems;
    }

    private SessionDatesSubject = new ReplaySubject<Array<SessionDates>>(1);
    private _sessiondates: Array<SessionDates>;
    @Input() set sessionDates(value: Array<SessionDates>) {
        if (value && value.length > 0) {
            this._sessiondates = value;
            this.SessionDatesSubject.next(value);

            this.sessionDatesSelectItem = this._sessiondates.map(x => ({
                value: x.SessionEnd,
                label: x.SessionEnd
            }));

            if (this.buildPressed == false) {
                this.editFormGroup.controls['SessionDates'].patchValue(
                    this.sessionDatesSelectItem[0].label
                );
            } else {
            }
            if (this.deletePressed == true) {
                this.editFormGroup.controls['SessionDates'].patchValue(
                    this.sessionDatesSelectItem[0].label
                );
            }
            if (this.cumulativeUpload == true) {
                this.cumulativeUpload = false;
                this.editFormGroup.controls['SessionDates'].patchValue(
                    moment(this.cumulativeUploadDate).format('MM/DD/YY')
                );
            }
            // if (this.editModeIsEnabled == true) {
            //     if (this.editFormGroup.controls['SessionDates'].value != null) {
            //         if (this.buildFormGroup.controls['Asofdate'].value != null) {
            //             this.editFormGroup.controls[
            //                 'SessionDates'
            //             ].patchValue(
            //                 moment(
            //                     this.buildFormGroup.controls['Asofdate']
            //                         .value
            //                 ).format('MM/DD/YY')
            //             );
            //             this.buildFormGroup.controls['Asofdate'].reset();
            //         }
            //     }
            // }
        }
    }
    get sessionDates(): Array<SessionDates> {
        return this._sessiondates;
    }

    @Input() public userGroups: Array<fromLookups.IUserGroup>;

    @Input() public process: Process;
    @Input() public IsFeatureLoading: boolean;
    @Output() LoadSessionDates = new ReplaySubject<any>(1);
    @Output() Timer = new ReplaySubject<any>(1);
    @Output() SaveContractCums = new ReplaySubject<ContractCumulative>(1);
    @Output() LoadContractCums = new ReplaySubject<any>(1);
    @Output() LoadAllContractCums = new ReplaySubject(1);
    @Output() CommitCumulatives = new ReplaySubject(1);
    @Output() DeleteContractCum = new ReplaySubject<ContractCumulative>(1);
    @Output() LoadDirectOrIndirect = new ReplaySubject<any>(1);
    public editing?: ContractCumulative = {} as ContractCumulative;
    clonedContractCums: { [i: string]: ContractCumulative } = {};
    active = true;
    moment: any;
    tableScrollHeight = '50em';
    buildFormGroup: UntypedFormGroup;
    editFormGroup: UntypedFormGroup;
    //txnDateFormGroup: UntypedFormGroup;
    //ccFormGroup: UntypedFormGroup;
    //taskFormGroup: UntypedFormGroup;
    //typeFormGroup: UntypedFormGroup;
    //siFormGroup: UntypedFormGroup;
    //empFormGroup: UntypedFormGroup;
    modDateFormGroup: UntypedFormGroup;
    showHideColumnsGroup: UntypedFormGroup;
    selectedContract: Contract;
    doi = 'Direct';
    txnTypesSelectItem: SelectItem[] = [];
    txnTypesInTableSelectItem: SelectItem[] = [];
    userGroupsSelectItem: SelectItem[] = [];
    userGroupsInTableSelectItem: SelectItem[] = [];
    tasksSelectItem: SelectItem[] = [];
    tasksInTableSelectItem: SelectItem[] = [];
    contractsSelectItem: SelectItem[] = [];
    directReportsSelectItem: SelectItem[] = [];
    serviceItemsSelectItem: SelectItem[] = [];
    sessionDatesSelectItem: SelectItem[] = [];
    siHidden = false;
    siColumnHidden = false;
    empHidden = false;
    empColumnHidden = false;
    selectedDirectReports: Users[] = [];
    selectedServiceItems: IServiceItem[] = [];
    selectedTasks: ITask[] = [];
    selectedCCs: IUserGroup[] = [];
    costCenters: CostCenter[] = [];

    asOfDateValid = false;
    contractValid = false;
    directReportValid = false;
    serviceItemValid = false;
    txnTypeValid = false;
    taskValid = false;
    ccValid = false;

    asOfDateIsRequired = false;
    contractIsRequired = false;
    directReportIsRequired = false;
    serviceItemIsRequired = false;
    txnTypeIsRequired = false;
    taskIsRequired = false;
    ccIsRequired = false;

    refreshTimer: any;

    showTimer = false;

    buildPressed = false;

    showRequiredMessage = false;

    editModeIsEnabled = false;

    isSettingUpEditMode = false;

    txnFilterHasValue = false;

    modFilterHasValue = false;

    tableItemSource: ContractCumulative[] = [];

    // sessionDates: { SessionDate: string }[] = [];

    selectedSessionDate: string;

    IsLoading = false;

    cols: any[];
    frozenCols: any[];

    length: number;
    header: string;

    SIColumnHidden = false;
    EmpColumnHidden = false;

    @ViewChild(Table, { static: true })
    ContractCumTable: Table;

    _selectedColumns: any[];
    @Input() get selectedColumns(): any[] {
        return this._selectedColumns;
    }

    set selectedColumns(val: any[]) {
        // restore original order
        this._selectedColumns = this.cols.filter(col => val.includes(col));
    }

    showUploadDialog = false;

    excelFileAsJsonStr: string;

    excelFileAsJson: any;

    showUploadError = false;

    @Output() uploadJSON = new ReplaySubject(1);

    @ViewChild('fileUpload') uploadInput: ElementRef;

    cumulativeUpload = false;
    cumulativeUploadDate: any;

    fromWorksheet = true;

    IsColumnFrozen = false;

    showManualAddDialog = false;

    constructor(private log: LoggingService) {
        super();
        this.moment = moment;
        this.buildFormGroup = new UntypedFormGroup({
            Asofdate: new UntypedFormControl(null),
            Contracts: new UntypedFormControl(null),
            Tasks: new UntypedFormControl(null),
            ServiceItems: new UntypedFormControl(null),
            DirectReports: new UntypedFormControl(null),
            Txntypes: new UntypedFormControl(null),
            CC: new UntypedFormControl(null),
            direct: new UntypedFormControl(null),
            indirect: new UntypedFormControl(null),
            worksheet: new UntypedFormControl(null),
            manual: new UntypedFormControl(null)
        });

        this.editFormGroup = new UntypedFormGroup({
            SessionDates: new UntypedFormControl(null),
            EditMode: new UntypedFormControl(null)
        });

        // this.txnDateFormGroup = new UntypedFormGroup({
        //     TxnDate: new UntypedFormControl(null)
        // });

        // this.ccFormGroup = new UntypedFormGroup({
        //     CC: new UntypedFormControl(null)
        // });

        this.modDateFormGroup = new UntypedFormGroup({
            ModDate: new UntypedFormControl(null)
        });

        // this.taskFormGroup = new UntypedFormGroup({
        //     Task: new UntypedFormControl(null)
        // });

        // this.typeFormGroup = new UntypedFormGroup({
        //     Type: new UntypedFormControl(null)
        // });

        // this.siFormGroup = new UntypedFormGroup({
        //     SI: new UntypedFormControl(null)
        // });

        // this.empFormGroup = new UntypedFormGroup({
        //     Emp: new UntypedFormControl(null)
        // });

        this.showHideColumnsGroup = new UntypedFormGroup({
            ShowHide: new UntypedFormControl(null)
        });
    }

    excelDateConversion(excelDate) {
        return new Date(Date.UTC(0, 0, excelDate - 1));
    }

    dateToISODateString(excelDate) {
        return this.excelDateConversion(excelDate).toISOString().split('T')[0];
    }

    ngOnInit() {
        if (this.userGroups.length > 0) {
            this.userGroups = this.userGroups.filter(x =>
                x.PrefixedGroupID.includes('C')
            );
            this.userGroupsSelectItem = this.userGroups.map(x => ({
                label: x.GroupName,
                value: x.GroupID
            }));
            this.userGroupsInTableSelectItem = this.userGroups.map(x => ({
                label: x.GroupName,
                value: x.GroupID
            }));
            this.log.verbose('IsFeatureLoading: ' + this.IsFeatureLoading);
            // this.userGroupsInTableSelectItem.unshift({
            //     label: "",
            //     value: 0
            // });
            // this.userGroupsSelectItem.unshift({
            //     label: "",
            //     value: 0
            // });

            // this.costCenters = this.userGroups.map(x => ({
            //     Costcenterid: x.GroupID,
            //     Costcenterdesc: x.GroupName
            // }));
        }
        this.frozenCols = [
            {
                field: '',
                header: `<i class="pi pi-question-circle" style="font-size: 1.65em" title="Status"></i>`
            },
            {
                field: '',
                header: '<i *ngIf="this.editModeIsEnabled == true" class="fa far fa-edit" style="font-size: 1.5em" title="Edit"></i>'
            },
            { field: 'Cumstatus', header: 'Status' },
            { field: 'Txndate', header: 'Txn Date' },
            { field: 'TaskLongName', header: 'Task' },
            { field: 'Txntype', header: 'Type' },
            { field: 'Costcenterid', header: 'Cost Center' }
        ];
        this.cols = [
            { field: 'Silistid', header: 'Item' },
            { field: 'Employeelistid', header: 'Person' }
        ];
        // { field: 'Hoursworked', header: 'Hours' },
        //     { field: 'Invoiceamount', header: 'Invoice' },
        //     { field: 'Frcost', header: 'FR Invoice' },
        //     { field: 'Ohcost', header: 'OH Invoice' },
        //     { field: 'Gacost', header: 'GA Invoice' },
        //     { field: 'Feecost', header: 'Fee Invoice' },
        //     { field: 'Actualamount', header: 'Incurred' },
        //     { field: 'Feecostebr', header: 'Fee Incurred' },
        //     { field: 'Modifieddate', header: 'Modified' },
        //     { field: '', header: 'Delete' },
        this._selectedColumns = this.cols;
        this.setupObservables();
        this.setupSubscriptions();

        this.buildFormGroup.controls['direct'].patchValue('direct');
        this.buildFormGroup.controls['worksheet'].patchValue('worksheet');
    }

    ngAfterViewInit(): void {
        this.IsColumnFrozen = true;
    }

    ngOnDestroy() {
        this.active = false;
        this.buildPressed = false;
    }

    onColumnResize($event) {
        const numOfFrozenCols = 7;
        const table = document.querySelector('table');
        columnResize($event, numOfFrozenCols, table);
        // var colIndex = $event.element.cellIndex + 1;
        // var change = $event.delta;

        // var table = document.querySelector('table');
        // var tableStyle = getComputedStyle(table);
        // var width = tableStyle.getPropertyValue("width");
        // var widthArr = width.split("p");
        // var widthValue = widthArr[0];
        // var newWidthValue = +widthValue + $event.delta;
        // table.style.width = `${newWidthValue}px`;
        // //expand
        // if (colIndex <= numOfFrozenCols) {
        //     for (var i = colIndex; i < numOfFrozenCols; i++) {
        //         var columnTdCells = Array.from(document.querySelectorAll(`td:nth-child(${i})`)) as Array<any>;
        //         for (var y = 0; y < columnTdCells.length; y++) {
        //             var style = getComputedStyle(columnTdCells[y].nextSibling);
        //             var left = style.getPropertyValue("left");
        //             var leftArr = left.split("p");
        //             var leftValue = leftArr[0];
        //             var newLeftValue;

        //             newLeftValue = +leftValue + change;
        //             columnTdCells[y].nextSibling.style.left = `${newLeftValue}px`;
        //         }
        //         var columnThCells = Array.from(document.querySelectorAll(`th:nth-child(${i})`)) as Array<any>;
        //         for (var z = 0; z < columnThCells.length; z++) {
        //             var style = getComputedStyle(columnThCells[z].nextSibling);
        //             var left = style.getPropertyValue("left");
        //             var leftArr = left.split("p");
        //             var leftValue = leftArr[0];
        //             var newLeftValue;

        //             newLeftValue = +leftValue + change;
        //             columnThCells[z].nextSibling.style.left = `${newLeftValue}px`;
        //         }
        //     }
        // }

        //fit
        // if (colIndex < numOfFrozenCols) {
        //     var columnTdCells = Array.from(document.querySelectorAll(`td:nth-child(${colIndex})`)) as Array<any>;
        //     for (var y = 0; y < columnTdCells.length; y++) {
        //         var style = getComputedStyle(columnTdCells[y].nextSibling);
        //         var left = style.getPropertyValue("left");
        //         var leftArr = left.split("p");
        //         var leftValue = leftArr[0];
        //         var newLeftValue;

        //         newLeftValue = +leftValue + change;
        //         columnTdCells[y].nextSibling.style.left = `${newLeftValue}px`;

        //     }
        //     var columnThCells = Array.from(document.querySelectorAll(`th:nth-child(${colIndex})`)) as Array<any>;
        //     for (var z = 0; z < columnThCells.length; z++) {
        //         var style = getComputedStyle(columnThCells[z].nextSibling);
        //         var left = style.getPropertyValue("left");
        //         var leftArr = left.split("p");
        //         var leftValue = leftArr[0];
        //         var newLeftValue;

        //         newLeftValue = +leftValue + change;
        //         columnThCells[z].nextSibling.style.left = `${newLeftValue}px`;

        //     }
        // }

        //$event.element.nextSibling.style.left = `${newLeftValue}px`;
        this.log.verbose($event);
    }

    showPersons() {
        if (this.EmpColumnHidden == true) {
            this.EmpColumnHidden = false;
        } else {
            this.EmpColumnHidden = true;
        }
    }
    showItems() {
        if (this.SIColumnHidden == true) {
            this.SIColumnHidden = false;
        } else {
            this.SIColumnHidden = true;
        }
    }

    onSelectedColumnsChange($event) {
        if ($event.value) {
            if ($event.itemValue) {
                if ($event.itemValue.header) {
                    if ($event.itemValue.header == 'Item') {
                        if (this.SIColumnHidden == true) {
                            this.SIColumnHidden = false;
                        } else {
                            this.SIColumnHidden = true;
                        }
                    }
                    if ($event.itemValue.header == 'Person') {
                        if (this.EmpColumnHidden == true) {
                            this.EmpColumnHidden = false;
                        } else {
                            this.EmpColumnHidden = true;
                        }
                    }
                    this.header = $event.itemValue.header;
                }
                this.length = $event.value.length;
            } else {
                if (this.length == 1) {
                    if (this.header == 'Item') {
                        if (this.SIColumnHidden == true) {
                            this.SIColumnHidden = false;
                        } else {
                            this.SIColumnHidden = true;
                        }
                    }
                    if (this.header == 'Person') {
                        if (this.EmpColumnHidden == true) {
                            this.EmpColumnHidden = false;
                        } else {
                            this.EmpColumnHidden = true;
                        }
                    }
                    this.length = 0;
                } else {
                    if (this.EmpColumnHidden == true) {
                        this.EmpColumnHidden = false;
                    } else {
                        this.EmpColumnHidden = true;
                    }
                    if (this.SIColumnHidden == true) {
                        this.SIColumnHidden = false;
                    } else {
                        this.SIColumnHidden = true;
                    }
                }
            }
        }
    }

    setupObservables() {
        this.log.verbose({
            type: 'ContractCumulativePresenterComponent:setupObservables'
        });
    }

    setupSubscriptions() {
        this.log.verbose({
            type: 'ContractCumulativePresenterComponent:setupSubscriptions'
        });

        // this.showHideColumnsGroup.controls['ShowHide'].valueChanges
        //     .pipe(takeWhile(_ => this.active))
        //     .subscribe(value => {
        //         if (value != null) {
        //             if (value.length == 1) {
        //                 if (value[0].header == "Service Item") {
        //                     if (this.SIColumnHidden == true) {
        //                         this.SIColumnHidden = false;
        //                     } else {
        //                         this.SIColumnHidden = true;
        //                     }
        //                 }
        //                 if (value[0].header == "Employee") {
        //                     if (this.EmpColumnHidden == true) {
        //                         this.EmpColumnHidden = false;
        //                     } else {
        //                         this.EmpColumnHidden = true;
        //                     }
        //                 }
        //             } else {
        //                 if (this.EmpColumnHidden == true) {
        //                     this.EmpColumnHidden = false;
        //                 } else {
        //                     this.EmpColumnHidden = true;
        //                 }

        //                 if (this.SIColumnHidden == true) {
        //                     this.SIColumnHidden = false;
        //                 } else {
        //                     this.SIColumnHidden = true;
        //                 }
        //             }

        //         }
        // });

        this.editFormGroup.controls['EditMode'].valueChanges
            .pipe(takeWhile(_ => this.active))
            .subscribe(value => {
                if (value != null) {
                    if (value == true) {
                        this.editModeIsEnabled = true;
                        if (
                            this.editFormGroup.controls['SessionDates'].value !=
                            null
                        ) {
                            // User already selected a session date - only overwrite if coming from build operation
                            if (
                                this.isSettingUpEditMode &&
                                this.buildFormGroup.controls['Asofdate'].value != null
                            ) {
                                this.IsLoading = true;
                                this.LoadSessionDates.next();
                                this.editFormGroup.controls[
                                    'SessionDates'
                                ].patchValue(
                                    moment(this.selectedAsOfDate).format(
                                        'MM/DD/YY'
                                    )
                                );
                                // Reset buildPressed flag after patching SessionDates to prevent loops
                                setTimeout(() => {
                                    this.buildPressed = false;
                                }, 200);
                            }
                            // If not from build operation, keep the existing session date
                        } else {
                            this.IsLoading = true;
                            if (
                                this.buildFormGroup.controls['Asofdate']
                                    .value != null
                            ) {
                                this.LoadSessionDates.next();
                                this.editFormGroup.controls[
                                    'SessionDates'
                                ].patchValue(
                                    moment(this.selectedAsOfDate).format(
                                        'MM/DD/YY'
                                    )
                                );
                                // Reset buildPressed flag after patching SessionDates to prevent loops
                                if (this.isSettingUpEditMode) {
                                    setTimeout(() => {
                                        this.buildPressed = false;
                                    }, 200);
                                }
                                // setTimeout(() => {
                                //     this.editFormGroup.controls[
                                //         'SessionDates'
                                //     ].patchValue(
                                //         moment(
                                //             this.selectedAsOfDate
                                //         ).format('MM/DD/YY')
                                //     );
                                // }, 2000)
                            } else {
                                this.LoadSessionDates.next();
                                // Reset buildPressed flag when no asofdate
                                if (this.isSettingUpEditMode) {
                                    setTimeout(() => {
                                        this.buildPressed = false;
                                    }, 200);
                                }
                            }
                        }
                    } else {
                        // this.editFormGroup.controls['SessionDates'].reset();
                        this.editModeIsEnabled = false;
                        this.leavingEditMode();
                        this.buildPressed = false;
                    }
                }
            });

        this.editFormGroup.controls['SessionDates'].valueChanges
            .pipe(takeWhile(_ => this.active))
            .subscribe(value => {
                if (value != null) {
                    this.tableItemSource = [];
                    this.selectedSessionDate = value;
                    if (this.buildPressed == true) {
                        setTimeout(() => {
                            this.LoadContractCums.next({
                                DirectOrIndirect: this.doi,
                                Todayonly: false,
                                Asofdate: value,
                                Tasks: null,
                                ServiceItems: null,
                                DirectReports: null,
                                Txntype: null,
                                CostCenters: null
                            });
                        }, 1000);
                    } else {
                        // Don't make API call if we're in the process of setting up edit mode
                        if (this.cumulativeUpload == false && !this.isSettingUpEditMode) {
                            this.LoadContractCums.next({
                                DirectOrIndirect: this.doi,
                                Todayonly: false,
                                Asofdate: value,
                                Tasks: null,
                                ServiceItems: null,
                                DirectReports: null,
                                Txntype: null,
                                CostCenters: null
                            });
                        }
                    }

                    if (this.deletePressed == true) {
                        this.LoadContractCums.next({
                            DirectOrIndirect: this.doi,
                            Todayonly: false,
                            Asofdate: value,
                            Tasks: null,
                            ServiceItems: null,
                            DirectReports: null,
                            Txntype: null,
                            CostCenters: null
                        });
                        this.deletePressed = false;
                    } else {
                    }

                    // this.tableItemSource = this.contractcums.filter(x => moment(x.Txndate).format("MM/DD/YYYY") == value);
                }
            });

        this.buildFormGroup.controls['worksheet'].valueChanges
            .pipe(takeWhile(_ => this.active))
            .subscribe(value => {
                if (value != null) {
                    this.fromWorksheet = true;
                    this.buildFormGroup.controls['manual'].patchValue(null);
                }

            });

        this.buildFormGroup.controls['manual'].valueChanges
            .pipe(takeWhile(_ => this.active))
            .subscribe(value => {
                if (value != null) {
                    this.fromWorksheet = false;
                    this.buildFormGroup.controls['worksheet'].patchValue(null);
                }

            });

        this.buildFormGroup.controls['Asofdate'].valueChanges
            .pipe(takeWhile(_ => this.active))
            .subscribe(value => {
                if (value != null) {
                    this.selectedAsOfDate = value;
                    this.asOfDateValid = true;
                    this.asOfDateIsRequired = false;
                }
            });

        this.buildFormGroup.controls['direct'].valueChanges
            .pipe(takeWhile(_ => this.active))
            .subscribe(value => {
                if (value != null) {
                    this.buildFormGroup.controls['indirect'].patchValue(null);

                    let filteredTxnTypes;
                    let filteredContracts;
                    let filteredTasks;
                    this.doi = 'Direct';

                    if (this.txntypes) {
                        filteredTxnTypes = this.txntypes.filter(
                            x => x.CostType == 'Direct'
                        );

                        this.txnTypesSelectItem = filteredTxnTypes.map(x => ({
                            value: x.CalcType,
                            label: x.CalcDesc
                        }));
                        this.txnTypesInTableSelectItem = filteredTxnTypes.map(
                            x => ({
                                value: x.CalcType,
                                label: x.CalcDesc
                            })
                        );
                    }
                    if (this.contracts) {
                        filteredContracts = this.contracts.filter(
                            x => x.ContractType != 'LOC'
                        );

                        this.contractsSelectItem = filteredContracts.map(x => ({
                            value: x.ContractListID,
                            label: x.ContractDesc
                        }));
                    }
                    if (this.tasks) {
                        filteredTasks = this.tasks.filter(
                            x => x.ContractType != 'LOC'
                        );

                        this.tasksSelectItem = filteredTasks.map(x => ({
                            value: x.TaskListID,
                            label: x.TaskLongName
                        }));
                        this.tasksInTableSelectItem = filteredTasks.map(x => ({
                            value: x.TaskListID,
                            label: x.TaskLongName
                        }));
                    }

                    this.LoadDirectOrIndirect.next({
                        DirectOrIndirect: this.doi,
                        Todayonly: false,
                        Asofdate: null,
                        Tasks: null,
                        ServiceItems: null,
                        DirectReports: null,
                        Txntype: null,
                        CostCenters: null
                    });
                }
            });
        this.buildFormGroup.controls['indirect'].valueChanges
            .pipe(takeWhile(_ => this.active))
            .subscribe(value => {
                if (value != null) {
                    this.buildFormGroup.controls['direct'].patchValue(null);

                    let filteredTxnTypes;
                    let filteredContracts;
                    let filteredTasks;

                    this.doi = 'Indirect';
                    if (this.txntypes) {
                        filteredTxnTypes = this.txntypes.filter(
                            x =>
                                x.CostType == 'Indirect' &&
                                x.CostSubType == 'Labor'
                        );

                        this.txnTypesSelectItem = filteredTxnTypes.map(x => ({
                            value: x.CalcType,
                            label: x.CalcDesc
                        }));
                        this.txnTypesInTableSelectItem = filteredTxnTypes.map(
                            x => ({
                                value: x.CalcType,
                                label: x.CalcDesc
                            })
                        );
                    }
                    if (this.contracts) {
                        filteredContracts = this.contracts.filter(
                            x => x.ContractType == 'LOC'
                        );

                        this.contractsSelectItem = filteredContracts.map(x => ({
                            value: x.ContractListID,
                            label: x.ContractDesc
                        }));
                    }
                    if (this.tasks) {
                        filteredTasks = this.tasks.filter(
                            x => x.ContractType == 'LOC'
                        );

                        this.tasksSelectItem = filteredTasks.map(x => ({
                            value: x.TaskListID,
                            label: x.TaskLongName
                        }));
                        this.tasksInTableSelectItem = filteredTasks.map(x => ({
                            value: x.TaskListID,
                            label: x.TaskLongName
                        }));
                    }

                    this.LoadDirectOrIndirect.next({
                        DirectOrIndirect: this.doi,
                        Todayonly: false,
                        Asofdate: null,
                        Tasks: null,
                        ServiceItems: null,
                        DirectReports: null,
                        Txntype: null,
                        CostCenters: null
                    });
                }
            });

        // this.buildFormGroup.controls['DoI'].valueChanges
        //     .pipe(takeWhile(_ => this.active))
        //     .subscribe(value => {
        //         if (value != null) {
        //             let filteredTxnTypes;
        //             let filteredContracts;
        //             let filteredTasks;
        //             if (value == false) {
        //                 this.doi = 'Direct';
        //                 filteredTxnTypes = this.txntypes.filter(
        //                     x => x.CostType == 'Direct'
        //                 );
        //                 filteredContracts = this.contracts.filter(
        //                     x => x.ContractType != 'LOC'
        //                 );
        //                 filteredTasks = this.tasks.filter(
        //                     x => x.ContractType != 'LOC'
        //                 );
        //             } else {
        //                 this.doi = 'Indirect';
        //                 filteredTxnTypes = this.txntypes.filter(
        //                     x =>
        //                         x.CostType == 'Indirect' &&
        //                         x.CostSubType == 'Labor'
        //                 );
        //                 filteredContracts = this.contracts.filter(
        //                     x => x.ContractType == 'LOC'
        //                 );
        //                 filteredTasks = this.tasks.filter(
        //                     x => x.ContractType == 'LOC'
        //                 );
        //             }
        //             this.contractsSelectItem = filteredContracts.map(x => ({
        //                 value: x.ContractListID,
        //                 label: x.ContractDesc
        //             }));
        //             this.tasksSelectItem = filteredTasks.map(x => ({
        //                 value: x.TaskListID,
        //                 label: x.TaskLongName
        //             }));
        //             this.tasksInTableSelectItem = filteredTasks.map(x => ({
        //                 value: x.TaskListID,
        //                 label: x.TaskLongName
        //             }));
        //             this.txnTypesSelectItem = filteredTxnTypes.map(x => ({
        //                 value: x.CalcType,
        //                 label: x.CalcDesc
        //             }));
        //             this.txnTypesInTableSelectItem = filteredTxnTypes.map(
        //                 x => ({
        //                     value: x.CalcType,
        //                     label: x.CalcDesc
        //                 })
        //             );

        //             this.LoadDirectOrIndirect.next({
        //                 DirectOrIndirect: this.doi,
        //                 Todayonly: null,
        //                 Asofdate: null,
        //                 Tasks: null,
        //                 ServiceItems: null,
        //                 DirectReports: null,
        //                 Txntype: null,
        //                 CostCenters: null
        //             });
        //         }
        //     });
        this.buildFormGroup.controls['Contracts'].valueChanges
            .pipe(takeWhile(_ => this.active))
            .subscribe(value => {
                if (value != null) {
                    this.buildFormGroup.controls['Tasks'].reset();
                    this.contractIsRequired = false;
                    this.contractValid = true;
                    this.selectedContract = this.contracts.find(
                        x => x.ContractListID == value
                    ) as Contract;
                    const filteredTasks = this.tasks.filter(x =>
                        x.TaskLongName.includes(
                            this.selectedContract.ContractDesc
                        )
                    );
                    this.tasksSelectItem = filteredTasks.map(x => ({
                        value: x.TaskListID,
                        label: x.TaskLongName
                    }));
                }
            });
        this.buildFormGroup.controls['Tasks'].valueChanges
            .pipe(takeWhile(_ => this.active))
            .subscribe(value => {
                if (value != null) {
                    this.selectedTasks = [];
                    this.taskValid = true;
                    this.taskIsRequired = false;
                    value.forEach(val => {
                        this.selectedTasks.push(
                            this.tasks.find(x => x.TaskListID == val) as any
                        );
                    });
                }
            });
        this.buildFormGroup.controls['Txntypes'].valueChanges
            .pipe(takeWhile(_ => this.active))
            .subscribe(value => {
                if (value != null) {
                    this.txnTypeValid = true;
                    this.txnTypeIsRequired = false;
                    const type = this.txntypes.find(
                        x => x.CalcType == value
                    ) as fromLookups.CalcType;
                    if (
                        value == 'T' ||
                        value == 'ODC' ||
                        value == 'MAT' ||
                        type.CostSubType == 'Expense'
                    ) {
                        this.siHidden = true;
                        this.empHidden = true;
                        this.serviceItemValid = true;
                        this.directReportValid = true;
                        // this.serviceItemIsRequired = false;
                        // this.directReportIsRequired = false;
                    }
                    if (value == 'SUB' || value == 'CON') {
                        this.siHidden = false;
                        this.empHidden = false;
                        this.directReportValid = true;
                        this.serviceItemValid = false;
                        // this.serviceItemIsRequired = true;
                        // this.directReportIsRequired = false;
                    }
                    if (type.CostSubType == 'Labor') {
                        this.siHidden = false;
                        this.empHidden = false;
                        this.directReportValid = false;
                        this.serviceItemValid = false;
                        // this.serviceItemIsRequired = true;
                        // this.directReportIsRequired = true;
                    }
                }
            });
        this.buildFormGroup.controls['CC'].valueChanges
            .pipe(takeWhile(_ => this.active))
            .subscribe(value => {
                if (value != null) {
                    this.selectedCCs = [];
                    this.ccValid = true;
                    this.ccIsRequired = false;
                    value.forEach(val => {
                        this.selectedCCs.push(
                            this.userGroups.find(
                                x => x.GroupID == val
                            ) as fromLookups.IUserGroup
                        );
                    });
                }
            });
        this.buildFormGroup.controls['ServiceItems'].valueChanges
            .pipe(takeWhile(_ => this.active))
            .subscribe(value => {
                if (value != null) {
                    this.selectedServiceItems = [];
                    this.serviceItemValid = true;
                    this.serviceItemIsRequired = false;
                    value.forEach(val => {
                        this.selectedServiceItems.push(
                            this.serviceitems.find(
                                x => x.SIListID == val
                            ) as fromLookups.IServiceItem
                        );
                    });
                }
            });
        this.buildFormGroup.controls['DirectReports'].valueChanges
            .pipe(takeWhile(_ => this.active))
            .subscribe(value => {
                if (value != null) {
                    this.selectedDirectReports = [];
                    this.directReportValid = true;
                    this.directReportIsRequired = false;
                    value.forEach(val => {
                        this.selectedDirectReports.push(
                            this.directreports.find(
                                x => x.EmployeeListID == val
                            ) as Users
                        );
                    });
                }
            });
    }

    // hideRequiredMessage() {
    //     asOfDateValid: boolean = false;
    // contractValid: boolean = false;
    // directReportValid: boolean = false;
    // serviceItemValid: boolean = false;
    // txnTypeValid: boolean = false;
    // taskValid: boolean = false;

    //     if (this.asOfDateValid) {

    //     }
    // }

    TxnDateFilter($event) {
        this.txnFilterHasValue = true;
        this.tableItemSource = this.tableItemSource.filter(
            x =>
                moment(x.Txndate).format('MM/DD/YYYY') ==
                moment($event).format('MM/DD/YYYY')
        );
    }

    ClearTxnDateFilter($event) {
        this.txnFilterHasValue = false;
        this.tableItemSource = this.contractcums.filter(x => x.Txndate != null);
    }

    ModDateFilter($event) {
        this.modFilterHasValue = true;
        this.tableItemSource = this.tableItemSource.filter(
            x =>
                moment(x.Modifieddate).format('MM/DD/YYYY') ==
                moment($event).format('MM/DD/YYYY')
        );
    }

    ClearModDateFilter($event) {
        this.modFilterHasValue = false;
        this.tableItemSource = this.contractcums.filter(
            x => x.Modifieddate != null
        );
    }

    findCostCenterName(Costcenterid: number): string | undefined {
        if (Costcenterid != null) {
            if (this.userGroups.length > 0) {
                if (Costcenterid !== 0) {
                    const cc = this.userGroups.find(
                        x => x.GroupID === Costcenterid
                    );
                    return cc?.GroupName;
                }
            }
        }
        return '';
    }

    findTask(rowData: ContractCumulative): string | undefined {
        if (rowData.Tasklistid != null) {
            if (this.tasks.length > 0) {
                const index = this.tasks.findIndex(
                    x => x.TaskListID === rowData.Tasklistid
                );
                if (index !== -1) {
                    const task = this.tasks[index];
                    return task.TaskLongName;
                }
            }
        }
        return '';
    }

    findEmployee(rowData: ContractCumulative): string | undefined {
        if (rowData.Employeelistid != null) {
            if (this.directreports.length > 0) {
                const index = this.directreports.findIndex(
                    x => x.EmployeeListID === rowData.Employeelistid
                );
                if (index !== -1) {
                    const directreport = this.directreports[index];
                    return directreport.EmployeeName;
                }
            }
        }
        return '';
    }

    findServiceItem(rowData: ContractCumulative): string | undefined {
        if (rowData.Silistid != null) {
            if (this.serviceitems.length > 0) {
                const index = this.serviceitems.findIndex(
                    x => x.SIListID === rowData.Silistid
                );
                if (index !== -1) {
                    const serviceitem = this.serviceitems[index];
                    return serviceitem.ServiceItem;
                }
            }
        }
        return '';
    }

    findTxnType(rowData: any): string | undefined {
        if (rowData != null) {
            if (this.txntypes.length > 0) {
                const index = this.txntypes.findIndex(
                    x => x.CalcType === rowData
                );
                if (index !== -1) {
                    const txntype = this.txntypes[index];
                    return txntype.CalcDesc;
                }
            }
        }
        return '';
    }

    onAsofdateFilterSelected($event) {
        const filteredCums: Array<ContractCumulative> =
            this.contractcums.filter(
                x =>
                    moment(new Date(x.Txndate)).format('MM/DD/YYYY') ==
                    moment($event).format('MM/DD/YYYY')
            );
        this.ContractCumsSubject.next(filteredCums);
    }

    onEditInit($event: { data: ContractCumulative }) {
        this.editing = { ...$event.data };
    }

    onRowEditInit(rowData: ContractCumulative) {
        this.clonedContractCums[rowData.Txnid] = { ...rowData };
        this.editing = rowData;
        this.log.verbose('onRowEditInit');
    }

    onRowEditSave(rowData: ContractCumulative) {
        this.SaveContractCums.next(rowData);
        delete this.clonedContractCums[rowData.Txnid];
        delete this.editing;
        this.log.verbose('onRowEditSave');
    }

    onRowEditCancel(item: ContractCumulative) {
        const index = this.tableItemSource.findIndex(
            x => x.Txnid === item.Txnid
        );
        if (index >= 0) {
            this.tableItemSource.splice(
                index,
                1,
                this.clonedContractCums[item.Txnid]
            );
        }
        this.tableItemSource = [...this.tableItemSource];

        delete this.clonedContractCums[item.Txnid];
        delete this.editing;
        this.log.verbose('onRowEditCancel');
    }

    onContractCumDelete(value) {
        this.IsLoading = true;
        this.deletePressed = true;
        this.DeleteContractCum.next(value);
        this.LoadSessionDates.next();
        // this.LoadContractCums.next({
        //     DirectOrIndirect: this.doi,
        //     Todayonly: false,
        //     Asofdate: this.editFormGroup.controls['SessionDates'].value,
        //     Tasks: null,
        //     ServiceItems: null,
        //     DirectReports: null,
        //     Txntype: null,
        //     CostCenters: null
        // });
    }

    filterTable($event, filterType) {
        // use this for all table filters
        if (filterType == 'serviceitem') {
            this.ContractCumTable.filter($event.value, 'Silistid', 'in');
        } else if (filterType == 'task') {
            this.ContractCumTable.filter($event.value, 'Tasklistid', 'in');
        } else if (filterType == 'type') {
            this.ContractCumTable.filter($event.value, 'Txntype', 'in');
        } else if (filterType == 'employee') {
            this.ContractCumTable.filter($event.value, 'Employeelistid', 'in');
        } else if (filterType == 'cc') {
            this.ContractCumTable.filter($event.value, 'Costcenterid', 'in');
        }
    }

    buildTable() {
        if (this.buildFormGroup.controls['Asofdate'].value == null) {
            this.asOfDateIsRequired = true;
        }
        if (this.buildFormGroup.controls['Contracts'].value == null) {
            this.contractIsRequired = true;
        }
        if (this.buildFormGroup.controls['Txntypes'].value == null) {
            this.txnTypeIsRequired = true;
        }
        if (this.buildFormGroup.controls['Tasks'].value == null) {
            this.taskIsRequired = true;
        }
        if (this.buildFormGroup.controls['CC'].value == null) {
            this.ccIsRequired = true;
        }

        if (this.userGroups.length > 1) {
            if (
                this.asOfDateValid == true &&
                this.contractValid == true &&
                this.txnTypeValid == true &&
                this.taskValid == true &&
                this.ccValid == true
            ) {
                this.buildPressed = true;
                // this.editFormGroup.controls['EditMode'].patchValue(true);
                if (this.buildFormGroup.controls['Contracts'].value != null) {
                    this.selectedContract = this.contracts.find(
                        x =>
                            x.ContractListID ==
                            this.buildFormGroup.controls['Contracts'].value
                    ) as Contract;
                    const filteredTasks = this.tasks.filter(x =>
                        x.TaskLongName.includes(
                            this.selectedContract.ContractDesc
                        )
                    );
                    this.tasksInTableSelectItem = filteredTasks.map(x => ({
                        value: x.TaskListID,
                        label: x.TaskLongName
                    }));
                }

                this.showRequiredMessage = false;

                this.LoadContractCums.next({
                    DirectOrIndirect: this.doi,
                    Todayonly: false,
                    Asofdate: this.buildFormGroup.controls['Asofdate'].value,
                    Tasks: this.selectedTasks,
                    ServiceItems: this.selectedServiceItems,
                    DirectReports: this.selectedDirectReports,
                    Txntype: this.buildFormGroup.controls['Txntypes'].value,
                    CostCenters: this.selectedCCs
                });

                this.selectedTasks = [];
                this.selectedServiceItems = [];
                this.selectedDirectReports = [];
                this.selectedCCs = [];

                //this.txnDateFormGroup.controls['TxnDate'].reset();
                this.modDateFormGroup.controls['ModDate'].reset();
                //this.taskFormGroup.controls['Task'].reset();
                //this.typeFormGroup.controls['Type'].reset();
                //this.siFormGroup.controls['SI'].reset();
                //this.empFormGroup.controls['Emp'].reset();
                //this.ccFormGroup.controls['CC'].reset();

                //this.buildFormGroup.controls['Asofdate'].reset();
                this.buildFormGroup.controls['Contracts'].reset();
                this.buildFormGroup.controls['Tasks'].reset();
                this.buildFormGroup.controls['Txntypes'].reset();
                this.buildFormGroup.controls['ServiceItems'].reset();
                this.buildFormGroup.controls['DirectReports'].reset();
                this.buildFormGroup.controls['CC'].reset();

                if (this.siHidden == true) {
                    this.siColumnHidden = true;
                }
                if (this.empHidden == true) {
                    this.empColumnHidden = true;
                }
            } else {
                this.showRequiredMessage = true;
            }
        } else {
            if (
                this.asOfDateValid == true &&
                this.contractValid == true &&
                this.txnTypeValid == true &&
                this.taskValid == true
            ) {
                this.buildPressed = true;
                // this.editFormGroup.controls['EditMode'].patchValue(true);
                if (this.buildFormGroup.controls['Contracts'].value != null) {
                    this.selectedContract = this.contracts.find(
                        x =>
                            x.ContractListID ==
                            this.buildFormGroup.controls['Contracts'].value
                    ) as Contract;
                    const filteredTasks = this.tasks.filter(x =>
                        x.TaskLongName.includes(
                            this.selectedContract.ContractDesc
                        )
                    );
                    this.tasksInTableSelectItem = filteredTasks.map(x => ({
                        value: x.TaskListID,
                        label: x.TaskLongName
                    }));
                }

                this.showRequiredMessage = false;

                this.LoadContractCums.next({
                    DirectOrIndirect: this.doi,
                    Todayonly: false,
                    Asofdate: this.buildFormGroup.controls['Asofdate'].value,
                    Tasks: this.selectedTasks,
                    ServiceItems: this.selectedServiceItems,
                    DirectReports: this.selectedDirectReports,
                    Txntype: this.buildFormGroup.controls['Txntypes'].value,
                    CostCenters: this.selectedCCs
                });

                this.selectedTasks = [];
                this.selectedServiceItems = [];
                this.selectedDirectReports = [];
                this.selectedCCs = [];

                //this.txnDateFormGroup.controls['TxnDate'].reset();
                this.modDateFormGroup.controls['ModDate'].reset();
                //this.taskFormGroup.controls['Task'].reset();
                //this.typeFormGroup.controls['Type'].reset();
                //this.siFormGroup.controls['SI'].reset();
                //this.empFormGroup.controls['Emp'].reset();
                //this.ccFormGroup.controls['CC'].reset();

                //this.buildFormGroup.controls['Asofdate'].reset();
                this.buildFormGroup.controls['Contracts'].reset();
                this.buildFormGroup.controls['Tasks'].reset();
                this.buildFormGroup.controls['Txntypes'].reset();
                this.buildFormGroup.controls['ServiceItems'].reset();
                this.buildFormGroup.controls['DirectReports'].reset();
                this.buildFormGroup.controls['CC'].reset();

                if (this.siHidden == true) {
                    this.siColumnHidden = true;
                }
                if (this.empHidden == true) {
                    this.empColumnHidden = true;
                }
            } else {
                this.showRequiredMessage = true;
            }
        }
    }

    showTodaysActivity() {
        if (this.doi == 'Direct') {
            this.tasksInTableSelectItem = this.tasks
                .filter(f => f.ContractType != 'LOC')
                .map(x => ({
                    value: x.TaskListID,
                    label: x.TaskLongName
                }));
            this.txnTypesInTableSelectItem = this.txntypes
                .filter(f => f.CalcType == 'Direct')
                .map(x => ({
                    value: x.CalcType,
                    label: x.CalcDesc
                }));
        } else {
            this.tasksInTableSelectItem = this.tasks
                .filter(f => f.ContractType == 'LOC')
                .map(x => ({
                    value: x.TaskListID,
                    label: x.TaskLongName
                }));
            this.txnTypesInTableSelectItem = this.txntypes
                .filter(f => f.CalcType == 'Indirect')
                .map(x => ({
                    value: x.CalcType,
                    label: x.CalcDesc
                }));
        }
        this.ContractCumTable.clear();
        if (this.txnFilterHasValue == true) {
            this.tableItemSource = this.contractcums.filter(
                x => x.Txndate != null
            );
        }

        //this.txnDateFormGroup.controls['TxnDate'].reset();
        this.modDateFormGroup.controls['ModDate'].reset();
        //this.taskFormGroup.controls['Task'].reset();
        //this.typeFormGroup.controls['Type'].reset();
        //this.siFormGroup.controls['SI'].reset();
        //this.empFormGroup.controls['Emp'].reset();
        //this.ccFormGroup.controls['CC'].reset();

        // var today = moment(new Date()).format("MM/DD/YYYY");
        // this.tableItemSource = this.tableItemSource.filter(x => moment(x.Modifieddate).format("MM/DD/YYYY") == today);
        this.LoadContractCums.next({
            DirectOrIndirect: this.doi,
            Todayonly: true,
            Asofdate: null,
            Tasks: null,
            ServiceItems: null,
            DirectReports: null,
            Txntype: null,
            CostCenters: null
        });
    }

    showAll() {
        if (this.doi == 'Direct') {
            this.tasksInTableSelectItem = this.tasks
                .filter(f => f.ContractType != 'LOC')
                .map(x => ({
                    value: x.TaskListID,
                    label: x.TaskLongName
                }));
            this.txnTypesInTableSelectItem = this.txntypes
                .filter(f => f.CostType == 'Direct')
                .map(x => ({
                    value: x.CalcType,
                    label: x.CalcDesc
                }));
        } else {
            this.tasksInTableSelectItem = this.tasks
                .filter(f => f.ContractType == 'LOC')
                .map(x => ({
                    value: x.TaskListID,
                    label: x.TaskLongName
                }));
            this.txnTypesInTableSelectItem = this.txntypes
                .filter(f => f.CostType == 'Indirect')
                .map(x => ({
                    value: x.CalcType,
                    label: x.CalcDesc
                }));
        }

        this.ContractCumTable.clear();
        //this.txnDateFormGroup.controls['TxnDate'].reset();
        this.modDateFormGroup.controls['ModDate'].reset();
        //this.taskFormGroup.controls['Task'].reset();
        //this.typeFormGroup.controls['Type'].reset();
        //this.siFormGroup.controls['SI'].reset();
        //this.empFormGroup.controls['Emp'].reset();
        //this.ccFormGroup.controls['CC'].reset();
        // this.tableItemSource = this.contractcums;

        // this.editFormGroup.controls['SessionDates'].patchValue(this.sessionDatesSelectItem[0].label);
        this.LoadContractCums.next({
            DirectOrIndirect: this.doi,
            Todayonly: false,
            Asofdate: null,
            Tasks: null,
            ServiceItems: null,
            DirectReports: null,
            Txntype: null,
            CostCenters: null
        });
    }

    leavingEditMode() {
        if (this.doi == 'Direct') {
            this.tasksInTableSelectItem = this.tasks
                .filter(f => f.ContractType != 'LOC')
                .map(x => ({
                    value: x.TaskListID,
                    label: x.TaskLongName
                }));
            this.txnTypesInTableSelectItem = this.txntypes
                .filter(f => f.CostType == 'Direct')
                .map(x => ({
                    value: x.CalcType,
                    label: x.CalcDesc
                }));
        } else {
            this.tasksInTableSelectItem = this.tasks
                .filter(f => f.ContractType == 'LOC')
                .map(x => ({
                    value: x.TaskListID,
                    label: x.TaskLongName
                }));
            this.txnTypesInTableSelectItem = this.txntypes
                .filter(f => f.CostType == 'Indirect')
                .map(x => ({
                    value: x.CalcType,
                    label: x.CalcDesc
                }));
        }

        this.ContractCumTable.clear();
        //this.txnDateFormGroup.controls['TxnDate'].reset();
        this.modDateFormGroup.controls['ModDate'].reset();
        //this.taskFormGroup.controls['Task'].reset();
        //this.typeFormGroup.controls['Type'].reset();
        //this.siFormGroup.controls['SI'].reset();
        //this.empFormGroup.controls['Emp'].reset();
        //this.ccFormGroup.controls['CC'].reset();
        // this.tableItemSource = this.contractcums;

        // this.editFormGroup.controls['SessionDates'].patchValue(this.sessionDatesSelectItem[0].label);
    }

    clearLinesWithout$() {
        this.ContractCumTable.clear();
        //this.txnDateFormGroup.controls['TxnDate'].reset();
        this.modDateFormGroup.controls['ModDate'].reset();
        //this.taskFormGroup.controls['Task'].reset();
        //this.typeFormGroup.controls['Type'].reset();
        //this.siFormGroup.controls['SI'].reset();
        //this.empFormGroup.controls['Emp'].reset();
        //this.ccFormGroup.controls['CC'].reset();

        if (this.tableItemSource.length > 0) {
            this.tableItemSource = this.tableItemSource.filter(x => {
                if (
                    x.Hoursworked == 0 &&
                    x.Actualamount == 0 &&
                    x.Feecost == 0 &&
                    x.Feecostebr == 0 &&
                    x.Frcost == 0 &&
                    x.Frcostebr == 0 &&
                    x.Gacost == 0 &&
                    x.Gacostebr == 0 &&
                    x.Invoiceamount == 0 &&
                    x.Ohcost == 0 &&
                    x.Ohcostebr == 0
                ) {
                } else {
                    return x;
                }
            });
        }
        // if (this.contractcums.length > 0) {
        //     this.contractcums.forEach((x, i) => {
        //         if (x.Actualamount == 0 && x.Feecost == 0 && x.Feecostebr == 0 && x.Frcost == 0 && x.Frcostebr == 0 && x.Gacost == 0 && x.Gacostebr == 0 && x.Invoiceamount == 0 && x.Ohcost == 0 && x.Ohcostebr == 0) {
        //             this.contractcums.splice(i);
        //         }
        //     });
        //     this.ContractCumsSubject.next(this.contractcums);
        // }
    }

    commitCumulatives() {
        this.LoadAllContractCums.next();
        setTimeout(() => {
            this.CommitCumulatives.next({
                DirectOrIndirect: 'Direct',
                Todayonly: false,
                Asofdate: this.editFormGroup.controls['SessionDates'].value,
                Tasks: null,
                ServiceItems: null,
                DirectReports: null,
                Txntype: null,
                CostCenters: null
            });
        }, 1000);
    }

    clearBuild() {
        this.selectedTasks = [];
        this.selectedServiceItems = [];
        this.selectedDirectReports = [];
        this.showRequiredMessage = false;
        this.asOfDateIsRequired = false;
        this.contractIsRequired = false;
        this.directReportIsRequired = false;
        this.serviceItemIsRequired = false;
        this.txnTypeIsRequired = false;
        this.ccIsRequired = false;
        this.taskIsRequired = false;
        this.empHidden = false;
        this.siHidden = false;
        this.asOfDateValid = false;
        this.contractValid = false;
        this.taskValid = false;
        this.txnTypeValid = false;
        this.ccValid = false;
        this.serviceItemValid = false;
        this.directReportValid = false;
        this.buildFormGroup.controls['Asofdate'].reset();
        this.buildFormGroup.controls['Contracts'].reset();
        this.buildFormGroup.controls['Tasks'].reset();
        this.buildFormGroup.controls['Txntypes'].reset();
        this.buildFormGroup.controls['ServiceItems'].reset();
        this.buildFormGroup.controls['DirectReports'].reset();
        this.buildFormGroup.controls['CC'].reset();
        if (this.doi == 'Direct') {
            this.tasksSelectItem = this.tasks
                .filter(f => f.ContractType != 'LOC')
                .map(x => ({
                    value: x.TaskListID,
                    label: x.TaskLongName
                }));
            this.tasksInTableSelectItem = this.tasks
                .filter(f => f.ContractType != 'LOC')
                .map(x => ({
                    value: x.TaskListID,
                    label: x.TaskLongName
                }));
            this.txnTypesInTableSelectItem = this.txntypes
                .filter(f => f.CalcType == 'Direct')
                .map(x => ({
                    value: x.CalcType,
                    label: x.CalcDesc
                }));
        } else {
            this.tasksSelectItem = this.tasks
                .filter(f => f.ContractType == 'LOC')
                .map(x => ({
                    value: x.TaskListID,
                    label: x.TaskLongName
                }));
            this.tasksInTableSelectItem = this.tasks
                .filter(f => f.ContractType == 'LOC')
                .map(x => ({
                    value: x.TaskListID,
                    label: x.TaskLongName
                }));
            this.txnTypesInTableSelectItem = this.txntypes
                .filter(f => f.CalcType == 'Indirect')
                .map(x => ({
                    value: x.CalcType,
                    label: x.CalcDesc
                }));
        }
    }

    openCumulativesReport() {
        const data = {
            reports: this.reports,
            queryparams: {
                StartDate: moment(new Date()).isValid()
                    ? moment(new Date()).format('YYYY-MM-DD')
                    : null,
                EndDate: moment(new Date()).isValid()
                    ? moment(new Date()).format('YYYY-MM-DD')
                    : moment(new Date()).isValid()
                    ? moment(new Date()).format('YYYY-MM-DD')
                    : null
            },
            params: {
                DirectReportList: [],
                ContractList: []
            }
        };
        // TODO: check if API wants this to be null or non-existent
        if (!data.queryparams.StartDate) {
            delete data.queryparams.StartDate;
        }
        this.RunReports.next(data);
    }

    openUploadDialog() {
        this.showUploadDialog = true;
    }

    uploadExcelFile($event) {
        this.showUploadError = false;
        this.fileToString($event.target.files[0]);
    }

    fileToString(file) {
        const fileNameSpan = document.getElementById('file-selected');
        fileNameSpan.textContent = file.name;

        const reader = new FileReader();
        reader.onload = event => {
            const binaryData = event.target.result;
            this.excelToJSON(binaryData);
        };
        reader.readAsBinaryString(file);
    }

    excelToJSON(binaryData) {
        const workbook = XLSX.read(binaryData, { type: 'binary' });

        const jsonData = workbook.SheetNames.reduce((initial, name) => {
            const sheet = workbook.Sheets[name];
            //Trimmed the keys of the json data
            initial = XLSX.utils.sheet_to_json(sheet).map(row => {
                const trimmed = {};
                Object.keys(row).forEach(key => {
                    trimmed[key.trim()] = row[key];
                });
                return trimmed;
            });
            return initial;
        }, {});

        this.excelFileAsJson = jsonData;
        //this.excelFileAsJsonStr = JSON.stringify(jsonData);
        //console.log(this.excelFileAsJsonStr);
    }

    sendJSON() {
        if (this.excelFileAsJson != null) {
            this.cumulativeUpload = true;
            this.excelFileAsJson = this.excelFileAsJson.map(x => ({
                ...x,
                PeriodEndDate: moment(this.dateToISODateString(x.PeriodEndDate)).format('MM/DD/YY')
            }));
            // this.excelFileAsJson[0] = {
            //     ...this.excelFileAsJson[0],
            //     PeriodEndDate: moment(this.dateToISODateString(this.excelFileAsJson[0].PeriodEndDate)).format('MM/DD/YY')
            // }
            this.cumulativeUploadDate = this.excelFileAsJson[0].PeriodEndDate;
            this.excelFileAsJsonStr = JSON.stringify(this.excelFileAsJson);
            this.uploadJSON.next(this.excelFileAsJsonStr);
            this.reset();
        } else {
            this.showUploadError = true;
            this.cumulativeUpload = false;
        }
    }

    cancelUpload() {
        this.showUploadError = false;
        this.reset();
    }

    reset() {
        this.showUploadError = false;
        this.showUploadDialog = false;
        this.excelFileAsJsonStr = null;
        this.uploadInput.nativeElement.value = '';
        const fileNameSpan = document.getElementById('file-selected');
        fileNameSpan.textContent = '';
    }
}
